import * as React from 'react';
import { keyBy } from 'lodash';
import Radio from '@material-ui/core/Radio';
import RadioGroup, { RadioGroupProps } from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';

import {
	Root,
	LabelLayout,
} from './styled';


const getSourceValue = (item: unknown, mapper: DataSourceMapperType) => mapper.getSourceValue(item);
const getSourceText = (item: unknown, mapper: DataSourceMapperType) => mapper.getSourceText(item);

const generateDataSourceByMapper = (items: Array<unknown>, mapper: DataSourceMapperType) => 
	items.map(item => ({ value: getSourceValue(item, mapper), name: getSourceText(item, mapper), origin: item }));

type DataSourceMapperType = {
	getSourceValue: (item: unknown) => string | number;
	getSourceText: (item: unknown) => string;
}

type DataSourceItemType = {
	value: number | string;
	name: string;
	origin: unknown;
}

type RadioButtonProps = {
	value: string | number;
	label: React.ReactNode;
	dataSource: Array<any>;
	dataSourceMapper?: DataSourceMapperType;
	disabled?: boolean;
	getValue?: (item: unknown) => string;
	onChange?: (e: React.ChangeEvent<{}>, value: string, item: unknown) => void;
} & Pick<RadioGroupProps,
	'row'
	| 'name'
	| 'style'
	| 'className'>

const RadioButton: React.SFC<RadioButtonProps> = (props) => {
	const {
		value,
		label,
		dataSource,
		dataSourceMapper: mapper,
		getValue,
		row,
		disabled,
		onChange,
		...rest
	} = props;
	const dataSourceMap = React.useMemo(() => keyBy(dataSource, s => getSourceValue(s, mapper)), [dataSource]);
	const handleChange = React.useCallback((e: React.ChangeEvent<{}>, value) => {
		const item = dataSourceMap[value];

		onChange(e, value, item);
	}, [dataSourceMap]);

	return (
		<FormControl component={Root}>
			<LabelLayout>
				<InputLabel shrink>{label}</InputLabel>
			</LabelLayout>
			<RadioGroup
				{...rest}
				value={getValue(value)}
				row={row}
				onChange={handleChange}>
				{
					generateDataSourceByMapper(dataSource, mapper).map(({ value, name }) => {
						return (
							<FormControlLabel
								key={value}
								value={value.toString()}
								disabled={disabled}
								control={<Radio color='primary' />}
								label={name} />
						);
					})
				}
			</RadioGroup>
		</FormControl>
	);
}

RadioButton.displayName = 'RadioButton';
RadioButton.defaultProps = {
	getValue: (item: string) => item ? item.toString() : '',
	dataSourceMapper: {
		getSourceValue: (item: DataSourceItemType) => item.value,
		getSourceText: (item: DataSourceItemType) => item.name
	},
}

export default RadioButton;
